import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, Eye, Save, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { TEST_IDS } from '@/constants/testIds/floreal';
import { errorMessage } from '@/lib/errorMessage';

// Editable template fields (map to ws_email_* settings keys).
const FIELDS = [
    { key: 'ws_email_subject', label: 'Oggetto email', type: 'input' },
    { key: 'ws_email_intro', label: 'Testo iniziale', rows: 3 },
    { key: 'ws_email_summary_title', label: 'Titolo riepilogo', type: 'input' },
    { key: 'ws_email_deposit_text', label: 'Testo pagamento / scadenza', rows: 3 },
    { key: 'ws_email_payment_title', label: 'Titolo istruzioni di pagamento', type: 'input' },
    { key: 'ws_payment_instructions', label: 'Coordinate di pagamento (predefinite)', rows: 3 },
    { key: 'ws_email_whatsapp_text', label: 'Testo WhatsApp / contabile', rows: 3 },
    { key: 'ws_email_closing', label: 'Testo finale', rows: 2 },
    { key: 'ws_email_signature', label: 'Firma', rows: 2 },
];

const VARIABLES = [
    'nome', 'cognome', 'nome_evento', 'tipologia_evento', 'data_evento', 'ora_evento',
    'numero_partecipanti', 'prezzo_per_persona', 'importo_per_persona', 'importo_totale',
    'giorni_pagamento', 'scadenza_pagamento', 'stato_prenotazione', 'istruzioni_pagamento', 'whatsapp',
];

export default function WorkshopEmailPanel() {
    const [form, setForm] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/site');
            const s = res.data?.settings || {};
            const next = { ws_whatsapp: s.ws_whatsapp || '' };
            FIELDS.forEach((f) => { next[f.key] = s[f.key] || ''; });
            setForm(next);
        } catch {
            toast.error('Impossibile caricare il template email');
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/admin/site/settings', { settings: form });
            toast.success('Template email salvato');
        } catch (e) {
            toast.error(errorMessage(e, 'Errore salvataggio'));
        } finally { setSaving(false); }
    };

    const runPreview = async () => {
        setPreviewing(true);
        try {
            // Preview uses CURRENT (possibly unsaved) edits via overrides, with sample data.
            const res = await api.post('/admin/workshop-email/preview', { overrides: form });
            setPreview(res.data);
        } catch (e) {
            toast.error(errorMessage(e, 'Errore anteprima'));
        } finally { setPreviewing(false); }
    };

    if (loading) {
        return (
            <div className="rounded-xl border border-border bg-card px-5 py-16 text-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Caricamento template…
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="font-serif text-2xl">Email workshop</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Personalizza l’email di conferma inviata automaticamente al cliente dopo la prenotazione. Tutte le sezioni sono modificabili.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={runPreview} disabled={previewing} data-testid={TEST_IDS.adminEmailPreview}>
                        {previewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}Anteprima
                    </Button>
                    <Button onClick={save} disabled={saving} data-testid={TEST_IDS.adminEmailSave}>
                        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Salva
                    </Button>
                </div>
            </div>

            {/* Variables helper */}
            <div className="rounded-xl border border-border bg-atelier-cream/40 p-4">
                <div className="flex items-center gap-2 text-sm font-medium mb-2">
                    <Info className="h-4 w-4 text-atelier-bronze" />Variabili disponibili
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                    Inserisci queste variabili nel testo: verranno sostituite automaticamente con i dati reali della prenotazione e dell’evento.
                </p>
                <div className="flex flex-wrap gap-1.5">
                    {VARIABLES.map((v) => (
                        <Badge key={v} variant="outline" className="text-[11px] font-mono border-atelier-sage/50 bg-atelier-sage/10 text-atelier-sage-ink">
                            {`{{${v}}}`}
                        </Badge>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Editor */}
                <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                    <div>
                        <Label className="eyebrow">Numero WhatsApp (per le email)</Label>
                        <Input
                            value={form.ws_whatsapp || ''}
                            onChange={(e) => setField('ws_whatsapp', e.target.value)}
                            className="mt-1.5"
                            placeholder="Es. 02 96779924"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Usato nella variabile {`{{whatsapp}}`}. Modificandolo, le future email useranno il nuovo numero.</p>
                    </div>
                    {FIELDS.map((f) => (
                        <div key={f.key}>
                            <Label className="eyebrow">{f.label}</Label>
                            {f.type === 'input' ? (
                                <Input
                                    data-testid={f.key === 'ws_email_subject' ? TEST_IDS.adminEmailSubject : undefined}
                                    value={form[f.key] || ''}
                                    onChange={(e) => setField(f.key, e.target.value)}
                                    className="mt-1.5"
                                />
                            ) : (
                                <Textarea
                                    rows={f.rows || 3}
                                    value={form[f.key] || ''}
                                    onChange={(e) => setField(f.key, e.target.value)}
                                    className="mt-1.5"
                                />
                            )}
                        </div>
                    ))}
                </div>

                {/* Preview */}
                <div className="rounded-xl border border-border bg-card p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-serif text-lg">Anteprima</h3>
                        <span className="text-xs text-muted-foreground">Dati di esempio</span>
                    </div>
                    {preview ? (
                        <div>
                            <div className="mb-3 text-sm">
                                <span className="text-muted-foreground">Oggetto: </span>
                                <span className="font-medium">{preview.subject}</span>
                            </div>
                            <iframe
                                title="Anteprima email"
                                srcDoc={preview.html}
                                className="w-full h-[520px] rounded-lg border border-border bg-white"
                            />
                        </div>
                    ) : (
                        <div className="py-20 text-center text-sm text-muted-foreground">
                            Premi “Anteprima” per vedere l’email con dati di esempio.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
